import { LocalStorageId } from '@resistapp/client/components/shared/local-storage';
import { useSearchParamsContext } from '@resistapp/client/contexts/search-params-context';
import { useUser } from '@resistapp/client/contexts/use-user-context';
import { useProject } from '@resistapp/client/hooks/api';
import { PathParams, usePathParam } from '@resistapp/client/hooks/use-path-params';
import { GeneGrouping, sixteenS } from '@resistapp/common/assays';
import { hasNormalisationModeData } from '@resistapp/common/normalisation-mode';
import { FullProject, FullSamplesByUID, NormalisationMode, type User, XAxisMode } from '@resistapp/common/types';
import {
  filterProjectForExternalSampleVisibility,
  flattenRelevantAbundances,
  flattenSamplesByUID,
} from '@resistapp/common/utils';
import { determineAvailableXAxisModes } from '@resistapp/common/x-axis-mode';
import { mapValues } from 'lodash';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import useLocalStorageState from 'use-local-storage-state';
import { QueryFilters, useQueryFilters } from '../hooks/use-query-filters/use-query-filters';
import { ApplicationErrors } from '../utils/error';
import { canUseResearchHeatmapGeneSetSelector } from '../utils/research-heatmap-abundance-mode';
import {
  defaultAvailableNormalisationModes,
  determineAvailableNormalisationModes,
  shouldSuppressVolumeModesForProject,
} from './use-overview-context/overview-context-utils';

export interface SampleData {
  data: FullProject | undefined;
  queryFilters: QueryFilters;
  availableNormalisationModes: NormalisationMode[];
  setAvailableNormalisationModesStable: React.Dispatch<React.SetStateAction<NormalisationMode[]>>;
  availableXAxisModes: XAxisMode[];
  setAvailableXAxisModesStable: React.Dispatch<React.SetStateAction<XAxisMode[]>>;
  loading: boolean;
  error: Error | null;
  refetchData: () => void;
}

const SampleDataContext = createContext<SampleData | undefined>(undefined);

function dropUnmeasured(samplesByUID: FullSamplesByUID): FullSamplesByUID {
  return mapValues(samplesByUID, reps =>
    reps.map(sample => ({
      ...sample,
      abundances: sample.abundances.filter(a => a.measured),
    })),
  );
}

function hasModeResult(abundances: ReturnType<typeof flattenRelevantAbundances>, mode: NormalisationMode): boolean {
  return abundances.some(abundance => hasNormalisationModeData(abundance, mode));
}

export function determineResearchAvailableNormalisationModes(
  focusedSamples: ReturnType<typeof flattenSamplesByUID> | undefined,
  forceSuppressVolumeModes: boolean,
  selectedTargetGrouping: GeneGrouping = 'l2Target',
): NormalisationMode[] {
  const baseModes = determineAvailableNormalisationModes(focusedSamples, undefined, {
    forceSuppressVolumeModes,
  });

  const onlyControlAssays = selectedTargetGrouping === sixteenS;
  const plottableAbundances = focusedSamples ? flattenRelevantAbundances(focusedSamples, onlyControlAssays) : [];
  const hasAyLitre =
    baseModes.includes(NormalisationMode.LITRE_16S) && hasModeResult(plottableAbundances, NormalisationMode.LITRE_AY);
  const hasAyAbsolute = hasModeResult(plottableAbundances, NormalisationMode.TEN_UL_DILUTED_DNA_AY);

  return baseModes.reduce<NormalisationMode[]>((acc, mode) => {
    acc.push(mode);
    if (mode === NormalisationMode.LITRE_16S && hasAyLitre) {
      acc.push(NormalisationMode.LITRE_AY);
    }
    if (mode === NormalisationMode.TEN_UL_DILUTED_DNA_16S && hasAyAbsolute) {
      acc.push(NormalisationMode.TEN_UL_DILUTED_DNA_AY);
    }
    return acc;
  }, []);
}

export function getEffectiveNormalisationMode(
  selectedMode: NormalisationMode,
  availableModes: NormalisationMode[],
): NormalisationMode {
  if (availableModes.length === 0) {
    return selectedMode;
  }
  return availableModes.includes(selectedMode) ? selectedMode : availableModes[0];
}

export function shouldUseExternalSampleVisibility(
  user: User | undefined | null,
  isAdmin: boolean,
  adminMode: boolean,
): boolean {
  return !adminMode && (isAdmin || user === undefined);
}

export function SampleDataProvider({ children }: { children: React.ReactNode }) {
  const projectId = usePathParam(PathParams.projectId);
  if (!projectId) {
    throw new Error(ApplicationErrors.projectID_is_missing);
  }
  const { data, isLoading, error, refetch } = useProject(projectId);

  // Keep unmeasured/estimated genes available only for the admin-only Research heatmap selector.
  const { searchParams } = useSearchParamsContext();
  const { isAdmin, user } = useUser();
  const [adminMode] = useLocalStorageState<boolean>(LocalStorageId.adminMode, {
    defaultValue: false,
  });
  const allowAll = searchParams.get('estimations') !== null && canUseResearchHeatmapGeneSetSelector(user, adminMode);
  const useExternalSampleVisibility = shouldUseExternalSampleVisibility(user, isAdmin, adminMode);
  const filteredData = useMemo(() => {
    if (!data) return undefined;
    const sampleVisibilityData = useExternalSampleVisibility ? filterProjectForExternalSampleVisibility(data) : data;
    const samplesByUID = allowAll
      ? sampleVisibilityData.samplesByUID
      : dropUnmeasured(sampleVisibilityData.samplesByUID);
    return { ...sampleVisibilityData, samplesByUID };
  }, [data, allowAll, useExternalSampleVisibility]);

  // const filteredData = useMemo(() => {
  //   return data ? { ...data, samplesByUID: dropUnmeasured(data.samplesByUID) } : undefined;
  // }, [data]);

  const { queryFilters, focusSamplesByUID } = useQueryFilters(filteredData);
  const focusedByUID = useMemo(() => {
    return !filteredData || !queryFilters.hasFocus
      ? filteredData?.samplesByUID
      : focusSamplesByUID(filteredData.samplesByUID);
  }, [filteredData, queryFilters.hasFocus, focusSamplesByUID]);

  const dataWithFocus = useMemo(() => {
    if (!filteredData) return undefined;
    return { ...filteredData, focusedByUID };
  }, [filteredData, focusedByUID]);
  const allVisibleSamples = useMemo(
    () => (filteredData ? flattenSamplesByUID(filteredData.samplesByUID) : undefined),
    [filteredData],
  );
  const focusedSamples = useMemo(() => (focusedByUID ? flattenSamplesByUID(focusedByUID) : undefined), [focusedByUID]);
  const forceSuppressVolumeModes = useMemo(
    () => shouldSuppressVolumeModesForProject(allVisibleSamples),
    [allVisibleSamples],
  );

  const [availableNormalisationModes, setAvailableNormalisationModesStable] = useState(
    defaultAvailableNormalisationModes,
  );
  useEffect(() => {
    const modes = determineResearchAvailableNormalisationModes(
      focusedSamples,
      forceSuppressVolumeModes,
      queryFilters.filters.selectedTargetGrouping,
    );
    setAvailableNormalisationModesStable(modes);
  }, [focusedSamples, forceSuppressVolumeModes, queryFilters.filters.selectedTargetGrouping]);

  useEffect(() => {
    if (availableNormalisationModes.length === 0) {
      return;
    }
    const selectedMode = queryFilters.filters.normalisationMode;
    const nextMode = getEffectiveNormalisationMode(selectedMode, availableNormalisationModes);
    if (nextMode === selectedMode) {
      return;
    }
    queryFilters.setNormalisationModeStable(nextMode);
  }, [availableNormalisationModes, queryFilters.filters.normalisationMode, queryFilters.setNormalisationModeStable]);

  const [availableXAxisModes, setAvailableXAxisModesStable] = useState<XAxisMode[]>([XAxisMode.ORDINAL_GROUPED]);
  useEffect(() => {
    const modes = determineAvailableXAxisModes(focusedSamples ?? []);
    setAvailableXAxisModesStable(modes);
  }, [focusedSamples]);

  const refetchData = useCallback(() => {
    void refetch();
  }, [refetch]);

  // Memoized so that the many consumers (every chart cell subscribes) only re-render when something changed
  const contextData = useMemo<SampleData>(
    () => ({
      loading: isLoading,
      error,
      data: dataWithFocus,
      availableNormalisationModes,
      setAvailableNormalisationModesStable,
      availableXAxisModes,
      setAvailableXAxisModesStable,
      refetchData,
      queryFilters,
    }),
    [isLoading, error, dataWithFocus, availableNormalisationModes, availableXAxisModes, refetchData, queryFilters],
  );

  return <SampleDataContext.Provider value={contextData}>{children}</SampleDataContext.Provider>;
}

export function useSampleDataContext() {
  const context = useContext(SampleDataContext);

  if (!context) {
    throw new Error('useSampleDataContext must be used within a SampleDataProvider');
  }

  return context;
}
